import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './component/ui/ProtectedRoute';
import DashboardLayout from './component/Layout/DashboardLayout';
import PageLoader from './component/ui/PageLoader';
import { getRoleHomePath } from './config/navigation';

// ── Existing pages ─────────────────────────────────────────────────────────────
const Login             = lazy(() => import('./pages/Login'));
const ForgotPassword    = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword     = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail       = lazy(() => import('./pages/VerifyEmail'));
const AcceptInvite      = lazy(() => import('./pages/AcceptInvite'));
const RequestAccess     = lazy(() => import('./pages/RequestAccess'));
const RequestAccessVerify = lazy(() => import('./pages/RequestAccessVerify'));
const Dashboard         = lazy(() => import('./pages/Dashboard'));
const Properties        = lazy(() => import('./pages/Properties'));
const PropertyDetail    = lazy(() => import('./pages/PropertyDetail'));
const Units             = lazy(() => import('./pages/Units'));
const Tenants           = lazy(() => import('./pages/Tenants'));
const Staff             = lazy(() => import('./pages/Staff'));
const ImportData        = lazy(() => import('./pages/ImportData'));
const Leases            = lazy(() => import('./pages/Leases'));
const Applications      = lazy(() => import('./pages/Applications'));
const Invoices          = lazy(() => import('./pages/Invoices'));
const Payments          = lazy(() => import('./pages/Payments'));
const MpesaLogs         = lazy(() => import('./pages/MpesaLogs'));
const Maintenance       = lazy(() => import('./pages/Maintenance'));
const Expenses          = lazy(() => import('./pages/Expenses'));
const Vendors           = lazy(() => import('./pages/Vendors'));
const Inspections       = lazy(() => import('./pages/Inspections'));
const Analytics         = lazy(() => import('./pages/Analytics'));
const Settings          = lazy(() => import('./pages/Settings'));
const Messages          = lazy(() => import('./pages/Messages'));
const UserManagement    = lazy(() => import('./pages/UserManagement'));
const PlatformDashboard = lazy(() => import('./pages/PlatformDashboard'));
const AuditLog          = lazy(() => import('./pages/AuditLog'));
const Prospects         = lazy(() => import('./pages/Prospects'));
const EstateDetail      = lazy(() => import('./pages/EstateDetail'));
const Collections       = lazy(() => import('./pages/Collections'));
const SecurityDeposits  = lazy(() => import('./pages/SecurityDeposits'));
const Utilities         = lazy(() => import('./pages/Utilities'));
const MyLease           = lazy(() => import('./pages/MyLease'));
const PaymentHistory    = lazy(() => import('./pages/PaymentHistory'));
const ReportIssue       = lazy(() => import('./pages/ReportIssue'));
const MyMaintenance     = lazy(() => import('./pages/MyMaintenance'));
const NoticeToVacate    = lazy(() => import('./pages/NoticeToVacate'));
const MyTasks           = lazy(() => import('./pages/MyTasks'));

// ── New pages ──────────────────────────────────────────────────────────────────
const VacateNotices     = lazy(() => import('./pages/VacateNotices'));
const VacancyListings   = lazy(() => import('./pages/VacancyListings'));
const Announcements     = lazy(() => import('./pages/Announcements'));
const Budgets           = lazy(() => import('./pages/Budgets'));
const ShortTermRentals  = lazy(() => import('./pages/ShortTermRentals'));
const AlertRules        = lazy(() => import('./pages/AlertRules'));

// ── Role constants ─────────────────────────────────────────────────────────────
const OWNER_ROLES  = ['superadmin', 'owner', 'manager'];
const ALL_ROLES    = ['superadmin', 'owner', 'manager', 'tenant', 'maintenance'];
const TENANT_MGMT  = ['superadmin', 'owner', 'manager', 'tenant'];

function RoleBasedRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  return <Navigate to={getRoleHomePath(user.role)} replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user)    return <Navigate to={getRoleHomePath(user.role)} replace />;
  return children;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries:   { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path="/reset-password"  element={<PublicRoute><ResetPassword /></PublicRoute>} />
              <Route path="/verify-email"    element={<PublicRoute><VerifyEmail /></PublicRoute>} />
              <Route path="/accept-invite"   element={<PublicRoute><AcceptInvite /></PublicRoute>} />
              <Route path="/request-access/verify" element={<PublicRoute><RequestAccessVerify /></PublicRoute>} />
              <Route path="/request-access"  element={<PublicRoute><RequestAccess /></PublicRoute>} />

              <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<RoleBasedRedirect />} />

                {/* ── Owner / Manager / Superadmin ── */}
                <Route path="dashboard"      element={<ProtectedRoute roles={OWNER_ROLES}><Dashboard /></ProtectedRoute>} />
                <Route path="properties"     element={<ProtectedRoute roles={OWNER_ROLES}><Properties /></ProtectedRoute>} />
                <Route path="properties/:id" element={<ProtectedRoute roles={OWNER_ROLES}><PropertyDetail /></ProtectedRoute>} />
                <Route path="units"          element={<ProtectedRoute roles={OWNER_ROLES}><Units /></ProtectedRoute>} />
                <Route path="tenants"        element={<ProtectedRoute roles={OWNER_ROLES}><Tenants /></ProtectedRoute>} />
                <Route path="staff"          element={<ProtectedRoute roles={['owner', 'superadmin']}><Staff /></ProtectedRoute>} />
                <Route path="import"         element={<ProtectedRoute roles={['owner', 'manager']}><ImportData /></ProtectedRoute>} />
                <Route path="leases"         element={<ProtectedRoute roles={OWNER_ROLES}><Leases /></ProtectedRoute>} />
                <Route path="applications"   element={<ProtectedRoute roles={OWNER_ROLES}><Applications /></ProtectedRoute>} />
                <Route path="invoices"       element={<ProtectedRoute roles={OWNER_ROLES}><Invoices /></ProtectedRoute>} />
                <Route path="payments"       element={<ProtectedRoute roles={OWNER_ROLES}><Payments /></ProtectedRoute>} />
                <Route path="mpesa-logs"     element={<ProtectedRoute roles={OWNER_ROLES}><MpesaLogs /></ProtectedRoute>} />
                <Route path="analytics"      element={<ProtectedRoute roles={OWNER_ROLES}><Analytics /></ProtectedRoute>} />
                <Route path="expenses"       element={<ProtectedRoute roles={OWNER_ROLES}><Expenses /></ProtectedRoute>} />
                <Route path="vendors"        element={<ProtectedRoute roles={OWNER_ROLES}><Vendors /></ProtectedRoute>} />
                <Route path="inspections"    element={<ProtectedRoute roles={OWNER_ROLES}><Inspections /></ProtectedRoute>} />
                <Route path="collections"    element={<ProtectedRoute roles={OWNER_ROLES}><Collections /></ProtectedRoute>} />
                <Route path="deposits"       element={<ProtectedRoute roles={OWNER_ROLES}><SecurityDeposits /></ProtectedRoute>} />
                <Route path="utilities"      element={<ProtectedRoute roles={OWNER_ROLES}><Utilities /></ProtectedRoute>} />

                {/* ── New pages ── */}
                <Route path="vacate-notices"   element={<ProtectedRoute roles={TENANT_MGMT}><VacateNotices /></ProtectedRoute>} />
                <Route path="vacancy-listings" element={<ProtectedRoute roles={OWNER_ROLES}><VacancyListings /></ProtectedRoute>} />
                <Route path="announcements"    element={<ProtectedRoute roles={OWNER_ROLES}><Announcements /></ProtectedRoute>} />
                <Route path="budgets"          element={<ProtectedRoute roles={OWNER_ROLES}><Budgets /></ProtectedRoute>} />
                <Route path="short-term"       element={<ProtectedRoute roles={OWNER_ROLES}><ShortTermRentals /></ProtectedRoute>} />
                <Route path="alert-rules"      element={<ProtectedRoute roles={OWNER_ROLES}><AlertRules /></ProtectedRoute>} />

                {/* ── Superadmin only ── */}
                <Route path="users"          element={<ProtectedRoute roles={['superadmin']}><UserManagement /></ProtectedRoute>} />
                <Route path="platform"       element={<ProtectedRoute roles={['superadmin']}><PlatformDashboard /></ProtectedRoute>} />
                <Route path="audit-logs" element={<ProtectedRoute roles={["superadmin"]}><AuditLog /></ProtectedRoute>} />
                <Route path="prospects"      element={<ProtectedRoute roles={['superadmin']}><Prospects /></ProtectedRoute>} />
                <Route path="platform/:id"   element={<ProtectedRoute roles={['superadmin']}><EstateDetail /></ProtectedRoute>} />

                {/* ── Maintenance staff + owners ── */}
                <Route path="maintenance"    element={<ProtectedRoute roles={['superadmin','owner','manager','maintenance']}><Maintenance /></ProtectedRoute>} />

                {/* ── All authenticated users ── */}
                <Route path="messages"       element={<ProtectedRoute roles={ALL_ROLES}><Messages /></ProtectedRoute>} />
                <Route path="settings"       element={<ProtectedRoute roles={ALL_ROLES}><Settings /></ProtectedRoute>} />

                {/* ── Tenant only ── */}
                <Route path="my-lease"         element={<ProtectedRoute roles={['tenant']}><MyLease /></ProtectedRoute>} />
                <Route path="payment-history"  element={<ProtectedRoute roles={['tenant']}><PaymentHistory /></ProtectedRoute>} />
                <Route path="report-issue"     element={<ProtectedRoute roles={['tenant']}><ReportIssue /></ProtectedRoute>} />
                <Route path="my-maintenance"   element={<ProtectedRoute roles={['tenant']}><MyMaintenance /></ProtectedRoute>} />
                <Route path="notice-to-vacate" element={<ProtectedRoute roles={['tenant']}><NoticeToVacate /></ProtectedRoute>} />

                {/* ── Maintenance staff only ── */}
                <Route path="my-tasks"         element={<ProtectedRoute roles={['maintenance']}><MyTasks /></ProtectedRoute>} />

                <Route path="*" element={<RoleBasedRedirect />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
